<template>
  <IntercomLayout>
    <template #topbar>
      <IntercomTopbar
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="ledger-view">
      <!-- Back link -->
      <button class="back-link" @click="router.push('/customers')">
        &larr; Back to Customers
      </button>

      <div v-if="pageLoading" class="loading-state">Loading...</div>

      <div v-else-if="customer" class="ledger-content">
        <!-- Customer Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ customer.name }}</h1>
            <p class="page-subtitle">Customer Ledger</p>
          </div>
          <div class="header-stats">
            <div class="stat-card">
              <span class="stat-label">Outstanding</span>
              <span class="stat-value" :class="(customer.outstanding_balance || 0) > 0 ? 'debit' : 'credit'">
                {{ formatCurrency(customer.outstanding_balance || 0) }}
              </span>
            </div>
            <div class="stat-card">
              <span class="stat-label">Credit Limit</span>
              <span class="stat-value">{{ customer.credit_limit ? formatCurrency(customer.credit_limit) : 'Unlimited' }}</span>
            </div>
            <div v-if="customer.phone" class="stat-card">
              <span class="stat-label">Phone</span>
              <span class="stat-value">{{ customer.phone }}</span>
            </div>
          </div>
        </div>

        <!-- Date Range Controls -->
        <IntercomCard>
          <div class="date-controls">
            <div class="quick-filters">
              <button
                v-for="preset in datePresets"
                :key="preset.label"
                class="preset-btn"
                :class="{ active: activePreset === preset.label }"
                @click="applyPreset(preset)"
              >
                {{ preset.label }}
              </button>
            </div>
            <div class="date-pickers">
              <IntercomDateInput
                v-model="fromDate"
                label="From"
                @update:model-value="handleDateChange"
              />
              <IntercomDateInput
                v-model="toDate"
                label="To"
                @update:model-value="handleDateChange"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- Summary Row -->
        <div v-if="!ledgerLoading && ledgerData" class="summary-row">
          <div class="summary-card">
            <span class="summary-label">Opening Balance</span>
            <span class="summary-value">{{ formatCurrency(ledgerData.opening_balance || 0) }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Total Sales (Dr)</span>
            <span class="summary-value debit">{{ formatCurrency(ledgerData.total_debit || 0) }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Total Received (Cr)</span>
            <span class="summary-value credit">{{ formatCurrency(ledgerData.total_credit || 0) }}</span>
          </div>
          <div class="summary-card highlight">
            <span class="summary-label">Closing Balance</span>
            <span class="summary-value" :class="(ledgerData.closing_balance || 0) > 0 ? 'debit' : 'credit'">
              {{ formatCurrency(Math.abs(ledgerData.closing_balance || 0)) }}
              {{ (ledgerData.closing_balance || 0) > 0 ? 'Dr' : 'Cr' }}
            </span>
          </div>
        </div>

        <!-- Ledger Table -->
        <IntercomCard>
          <div v-if="ledgerLoading" class="table-loading">Loading transactions...</div>
          <div v-else-if="entries.length === 0" class="table-empty">
            No transactions found in this period.
          </div>
          <div v-else class="ledger-table-wrap">
            <table class="ledger-table">
              <thead>
                <tr>
                  <th class="col-date">Date</th>
                  <th class="col-type">Type</th>
                  <th class="col-ref">Reference</th>
                  <th class="col-desc">Description</th>
                  <th class="col-num">Debit (Dr)</th>
                  <th class="col-num">Credit (Cr)</th>
                  <th class="col-num">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, idx) in entries" :key="idx" class="ledger-row">
                  <td class="col-date">{{ formatDate(entry.date) }}</td>
                  <td class="col-type">
                    <span class="type-badge" :class="`type-${entry.type}`">
                      {{ formatEntryType(entry.type) }}
                    </span>
                  </td>
                  <td class="col-ref">{{ entry.reference || '-' }}</td>
                  <td class="col-desc">{{ entry.description || '-' }}</td>
                  <td class="col-num">
                    <span v-if="entry.debit > 0" class="debit">{{ formatCurrency(entry.debit) }}</span>
                    <span v-else class="empty-cell">-</span>
                  </td>
                  <td class="col-num">
                    <span v-if="entry.credit > 0" class="credit">{{ formatCurrency(entry.credit) }}</span>
                    <span v-else class="empty-cell">-</span>
                  </td>
                  <td class="col-num">
                    <span :class="entry.running_balance > 0 ? 'debit' : 'credit'">
                      {{ formatCurrency(Math.abs(entry.running_balance)) }}
                      <span class="dr-cr-label">{{ entry.running_balance > 0 ? 'Dr' : 'Cr' }}</span>
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="totals-row">
                  <td colspan="4">Totals</td>
                  <td class="col-num debit">{{ formatCurrency(ledgerData?.total_debit || 0) }}</td>
                  <td class="col-num credit">{{ formatCurrency(ledgerData?.total_credit || 0) }}</td>
                  <td class="col-num">
                    <span :class="(ledgerData?.closing_balance || 0) > 0 ? 'debit' : 'credit'">
                      {{ formatCurrency(Math.abs(ledgerData?.closing_balance || 0)) }}
                      <span class="dr-cr-label">{{ (ledgerData?.closing_balance || 0) > 0 ? 'Dr' : 'Cr' }}</span>
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="table-footer">
            <span class="entry-count">{{ entries.length }} transaction{{ entries.length !== 1 ? 's' : '' }}</span>
          </div>
        </IntercomCard>
      </div>

      <div v-else class="empty-state">Customer not found</div>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomCard,
  IntercomDateInput,
} from '@shared/components/intercom'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const route = useRoute()
const router = useRouter()
const { user, logout } = useAuth()
const { getCustomer, getCustomerLedger, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// Page state
const pageLoading = ref(true)
const ledgerLoading = ref(false)
const customer = ref(null)
const ledgerData = ref(null)
const activePreset = ref('1M')

// Date range — default to last 1 month
const today = new Date()
const oneMonthAgo = new Date(today)
oneMonthAgo.setMonth(today.getMonth() - 1)

const fromDate = ref(oneMonthAgo.toISOString().split('T')[0])
const toDate = ref(today.toISOString().split('T')[0])

// Quick-select presets
const datePresets = [
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: 'FY', fy: true },
  { label: 'All', all: true },
]

const entries = computed(() => {
  if (!ledgerData.value?.entries) return []
  return ledgerData.value.entries
})

const applyPreset = (preset) => {
  activePreset.value = preset.label
  const end = new Date()
  toDate.value = end.toISOString().split('T')[0]

  if (preset.all) {
    // Go back 10 years to capture everything
    const start = new Date(end)
    start.setFullYear(start.getFullYear() - 10)
    fromDate.value = start.toISOString().split('T')[0]
  } else if (preset.fy) {
    // Indian financial year: April 1 to March 31
    const now = new Date()
    const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
    fromDate.value = `${fyStartYear}-04-01`
  } else {
    const start = new Date(end)
    start.setMonth(start.getMonth() - preset.months)
    fromDate.value = start.toISOString().split('T')[0]
  }

  fetchLedger()
}

const handleDateChange = () => {
  activePreset.value = ''
  fetchLedger()
}

const fetchLedger = async () => {
  if (!customer.value) return
  ledgerLoading.value = true
  try {
    const res = await getCustomerLedger(customer.value.id, fromDate.value, toDate.value)
    ledgerData.value = res
  } catch (error) {
    console.error('Failed to fetch ledger:', error)
    ledgerData.value = { entries: [], total_debit: 0, total_credit: 0, closing_balance: 0, opening_balance: 0 }
  } finally {
    ledgerLoading.value = false
  }
}

const formatCurrency = (value) => {
  if (value == null) return '-'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(value)
}

const formatEntryType = (type) => {
  const labels = {
    invoice: 'Invoice',
    payment: 'Payment',
    credit_note: 'Credit Note',
    debit_note: 'Debit Note',
    opening_balance: 'Opening Bal.',
    adjustment: 'Adjustment',
  }
  return labels[type] || type
}

const handleLogout = () => {
  logout()
  router.push('/login')
}

onMounted(async () => {
  fetchBusinessName()
  const customerId = route.params.id
  try {
    const res = await getCustomer(customerId)
    customer.value = res.customer || res
    await fetchLedger()
  } catch (error) {
    console.error('Failed to load customer:', error)
  } finally {
    pageLoading.value = false
  }
})
</script>

<style scoped>
.ledger-view {
  padding: var(--space-6);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: var(--text-sm);
  padding: 0;
  margin-bottom: var(--space-4);
}
.back-link:hover {
  color: var(--color-text-primary);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: var(--space-16);
  color: var(--color-text-secondary);
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-6);
  gap: var(--space-4);
  flex-wrap: wrap;
}
.page-title {
  font-size: var(--text-xl);
  font-weight: 600;
  margin: 0;
}
.page-subtitle {
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  margin: var(--space-1) 0 0;
}

.header-stats {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}
.stat-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
  min-width: 120px;
}
.stat-label {
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.stat-value {
  font-size: var(--text-sm);
  font-weight: 600;
}

/* Date controls */
.date-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
  padding: var(--space-3) var(--space-4);
}
.quick-filters {
  display: flex;
  gap: var(--space-1);
}
.preset-btn {
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--color-text-secondary);
  transition: all 0.15s;
}
.preset-btn:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
.preset-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}
.date-pickers {
  display: flex;
  gap: var(--space-3);
  align-items: end;
}

/* Summary row */
.summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3);
  margin: var(--space-4) 0;
}
.summary-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.summary-card.highlight {
  border-color: var(--color-primary);
  background: var(--color-primary-50, #eff6ff);
}
.summary-label {
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
}
.summary-value {
  font-size: var(--text-base);
  font-weight: 600;
}

/* Table */
.table-loading,
.table-empty {
  text-align: center;
  padding: var(--space-10);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
}
.ledger-table-wrap {
  overflow-x: auto;
}
.ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}
.ledger-table th {
  text-align: left;
  padding: var(--space-2) var(--space-3);
  border-bottom: 2px solid var(--color-border);
  font-weight: 600;
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-secondary);
  white-space: nowrap;
}
.ledger-table td {
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--color-border-light, #f0f0f0);
}
.ledger-row:hover {
  background: var(--color-bg-secondary);
}
.col-date { width: 100px; }
.col-type { width: 100px; }
.col-ref { width: 140px; }
.col-desc { }
.col-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.empty-cell {
  color: var(--color-text-tertiary, #ccc);
}

/* Type badges */
.type-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: var(--radius-full, 999px);
  font-size: var(--text-xs);
  font-weight: 500;
}
.type-invoice { background: #fef3c7; color: #92400e; }
.type-payment { background: #d1fae5; color: #065f46; }
.type-credit_note { background: #dbeafe; color: #1e40af; }
.type-debit_note { background: #fce7f3; color: #9d174d; }

/* Totals */
.totals-row {
  font-weight: 600;
  background: var(--color-bg-secondary);
}
.totals-row td {
  padding: var(--space-3);
  border-top: 2px solid var(--color-border);
}
.table-footer {
  padding: var(--space-2) var(--space-4);
  text-align: right;
}
.entry-count {
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
}

/* Colors */
.debit { color: #dc2626; }
.credit { color: #16a34a; }
.dr-cr-label {
  font-size: var(--text-xs);
  opacity: 0.7;
  margin-left: 2px;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header { flex-direction: column; }
  .header-stats { width: 100%; }
  .summary-row { grid-template-columns: repeat(2, 1fr); }
  .date-controls { flex-direction: column; align-items: stretch; }
  .date-pickers { flex-direction: column; }
}
</style>
